<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import authAPI from 'dashboard/api/auth';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();
const sessions = ref([]);
const loading = ref(false);

const relativeTime = dateStr => {
  if (!dateStr) return '';
  return formatDistanceToNow(parseISO(dateStr), { addSuffix: true });
};

const isUnknown = val => !val || val === 'Unknown' || val === 'Unknown Browser';

const deviceIcon = session => {
  const name = (session.device_name || '').toLowerCase();
  if (
    name.includes('iphone') ||
    name.includes('android') ||
    name.includes('mobile')
  ) {
    return 'i-lucide-smartphone';
  }
  if (name.includes('ipad') || name.includes('tablet')) {
    return 'i-lucide-tablet';
  }
  return 'i-lucide-monitor';
};

const sessionLabel = session => {
  const parts = [];
  if (!isUnknown(session.browser_name)) {
    parts.push(
      session.browser_version
        ? `${session.browser_name} ${session.browser_version}`
        : session.browser_name
    );
  }
  if (!isUnknown(session.platform_name)) parts.push(session.platform_name);
  return (
    parts.join(' on ') ||
    t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.UNKNOWN_DEVICE')
  );
};

const locationLabel = session => {
  const parts = [];
  if (session.city) parts.push(session.city);
  if (session.country) parts.push(session.country);
  return parts.join(', ');
};

// Phase 9.5: the kit's table ("Your active sessions"): device, place, last activity and, on every session but
// this one, "Revoke" in the pinned last column.
const headers = computed(() => [
  t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.TITLE'),
  t('SETTINGS_PIECES.SESSIONS.LOCATION'),
  t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.LAST_ACTIVE'),
  t('SETTINGS_TABLE.ACTIONS'),
]);

const fetchSessions = async () => {
  loading.value = true;
  try {
    const { data } = await authAPI.getSessions();
    sessions.value = data;
  } catch {
    useAlert(t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const revokeSession = async session => {
  try {
    await authAPI.revokeSession(session.id);
    sessions.value = sessions.value.filter(s => s.id !== session.id);
    useAlert(t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.REVOKE_SUCCESS'));
  } catch {
    useAlert(t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.REVOKE_ERROR'));
  }
};

onMounted(fetchSessions);
</script>

<template>
  <BaseTable :headers="headers" :items="sessions" pin-last-column>
    <template #row="{ items }">
      <BaseTableRow v-for="session in items" :key="session.id" :item="session">
        <BaseTableCell>
          <span class="flex min-w-0 items-center gap-2">
            <Icon
              :icon="deviceIcon(session)"
              class="size-4 shrink-0 text-polaris-muted-foreground"
            />
            <span class="truncate">{{ sessionLabel(session) }}</span>
            <Badge v-if="session.current" variant="secondary">
              {{ $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.CURRENT') }}
            </Badge>
          </span>
        </BaseTableCell>
        <BaseTableCell>{{ locationLabel(session) || '—' }}</BaseTableCell>
        <BaseTableCell>
          {{ relativeTime(session.last_activity_at) }}
        </BaseTableCell>
        <BaseTableCell actions>
          <Button
            v-if="!session.current"
            type="button"
            outline
            sm
            color="ruby"
            :label="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.REVOKE')"
            @click="revokeSession(session)"
          />
        </BaseTableCell>
      </BaseTableRow>
    </template>
  </BaseTable>
</template>
